import {
  CARRIER_WIZARD_STEPS,
  DRIVER_ACTION_NEEDED_STATUSES,
  DRIVER_STATUS,
  MILLIS,
  PIPELINE_EXPIRING_WITHIN_DAYS,
  STANDARD_DRIVER_ONBOARDING_STEPS,
  type PipelineKind,
  type PipelineStage,
} from '@repo/constants'

export type PipelineStageInput = {
  kind: PipelineKind
  now: Date
  token: { expiresAt: Date; revokedAt: Date | null } | null
  application: { status: string; phase: string | null; currentStepKey: string | null } | null
  // Owner operator: whether any of the application's tokens was spent. The
  // office makes the account at invite time (`OO-50`), so `account_created`
  // alone does not mean the invitee came.
  tokenSpent: boolean
  driverStatus: string | null
  // Carrier: a live envelope the carrier has signed and the office has not.
  carrierAwaitingCountersign: boolean
}

const OWNER_OPERATOR_CLOSED_DRIVER_STATUSES: readonly string[] = [
  DRIVER_STATUS.REJECTED,
  DRIVER_STATUS.INACTIVE,
  DRIVER_STATUS.CLOSED_NO_INSURANCE,
  DRIVER_STATUS.CLOSED_AGENT_TIMEOUT,
]

// "The office's turn" on the owner operator's agreement (`DRV-08`);
// `signature_driver_completed` is retired as a target but rows may still sit there.
const OWNER_OPERATOR_COUNTERSIGN_DRIVER_STATUSES: readonly string[] = [
  DRIVER_STATUS.AWAITING_COUNTERSIGNATURE,
  DRIVER_STATUS.SIGNATURE_DRIVER_COMPLETED,
]

// Both signatures in, the office owes the approval.
const OWNER_OPERATOR_REVIEW_DRIVER_STATUSES: readonly string[] = [
  ...DRIVER_ACTION_NEEDED_STATUSES.filter(
    (status) => !OWNER_OPERATOR_COUNTERSIGN_DRIVER_STATUSES.includes(status),
  ),
  DRIVER_STATUS.SIGNATURE_COMPLETED,
]

/** Whether an invitation's link still opens: not revoked, not expired. */
export const isInviteLinkLive = (
  token: { expiresAt: Date | string; revokedAt: Date | string | null },
  now: Date = new Date(),
) => !token.revokedAt && new Date(token.expiresAt) >= now

const isTokenDead = (input: PipelineStageInput) =>
  !!input.token && !isInviteLinkLive(input.token, input.now)

const ownerOperatorStage = (input: PipelineStageInput): PipelineStage => {
  const { application, driverStatus } = input
  if (driverStatus === DRIVER_STATUS.APPROVED_FOR_DISPATCH) return 'approved'
  if (driverStatus && OWNER_OPERATOR_CLOSED_DRIVER_STATUSES.includes(driverStatus)) return 'closed'
  const notStarted =
    !application ||
    application.status === 'invited' ||
    (application.status === 'account_created' && !input.tokenSpent)
  if (notStarted) return isTokenDead(input) ? 'closed' : 'invited'
  if (driverStatus && OWNER_OPERATOR_COUNTERSIGN_DRIVER_STATUSES.includes(driverStatus)) {
    return 'countersign'
  }
  if (driverStatus && OWNER_OPERATOR_REVIEW_DRIVER_STATUSES.includes(driverStatus)) {
    return 'needs_review'
  }
  return 'applying'
}

// A carrier's application sits at `invited` until it submits, and its link is
// the only way back in (`ONB-19`), so a dead link closes it either way.
const carrierStage = (input: PipelineStageInput): PipelineStage => {
  const { application } = input
  if (!application || application.status === 'invited') {
    if (isTokenDead(input)) return 'closed'
    const started =
      !!application?.currentStepKey && application.currentStepKey !== CARRIER_WIZARD_STEPS[0]
    return started ? 'applying' : 'invited'
  }
  return input.carrierAwaitingCountersign ? 'countersign' : 'needs_review'
}

/** One person's place in the office's onboarding pipeline (`ONB-32`). */
export function derivePipelineStage(input: PipelineStageInput): PipelineStage {
  const phase = input.application?.phase
  if (phase === 'rejected' || phase === 'closed') return 'closed'
  if (phase === 'approved') return 'approved'
  return input.kind === 'owner_operator' ? ownerOperatorStage(input) : carrierStage(input)
}

export function isInviteExpiringSoon(
  token: { expiresAt: Date; revokedAt: Date | null },
  now: Date,
): boolean {
  if (!isInviteLinkLive(token, now)) return false
  return token.expiresAt.getTime() - now.getTime() <= PIPELINE_EXPIRING_WITHIN_DAYS * MILLIS.DAY
}

export function pipelineSteps(kind: PipelineKind): readonly string[] {
  return kind === 'owner_operator' ? STANDARD_DRIVER_ONBOARDING_STEPS : CARRIER_WIZARD_STEPS
}

/** Steps behind the person, for the list's segmented bar. */
export function pipelineStepsDone(
  kind: PipelineKind,
  stage: PipelineStage,
  currentStepKey: string | null,
): number {
  const steps = pipelineSteps(kind)
  if (stage === 'approved') return steps.length
  if (stage === 'invited' || !currentStepKey) return 0
  const index = steps.indexOf(currentStepKey)
  return index === -1 ? 0 : index
}
